import { Trans, useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button.tsx";
import { Notification } from "@/components/Interface/Notification/Notification.tsx";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer.tsx";

export interface NewGroupCardProps {
  dataTestid?: string;
  customerName?: string;
  isSubmitting: boolean;
  onCreateGroup: () => void;
}

const stepKeys = ["steps.1", "steps.2", "steps.3"] as const;

/**
 * What the customer gets out of a group, how it works in three
 * steps, and the single action that creates it.
 */
export const NewGroupCard = ({
  dataTestid,
  customerName,
  isSubmitting,
  onCreateGroup,
}: NewGroupCardProps) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits.newGroup",
  });

  const highlight = <span className="font-semibold text-foreground-info" />;

  return (
    <section className="w-full">
      <CardContainer
        dataTestid={dataTestid}
        className="flex w-full flex-col gap-4 rounded-lg p-4"
        variant="ivory"
      >
        <div className="flex w-full items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 p-2 leading-none">
            <p className="text-body-base font-semibold tracking-[0.32px] text-foreground-primary">
              {t("title")}
            </p>
            <p className="text-body-sm tracking-[0.28px] text-foreground-secondary">
              {t("subtitle", { name: customerName })}
            </p>
          </div>
          <Button
            dataTestid="fwb-create-group"
            variant="primary"
            size="sm"
            disabled={isSubmitting}
            onClick={onCreateGroup}
          >
            {t("createButton")}
          </Button>
        </div>

        <Notification
          dataTestid="fwb-new-group-hint"
          title={
            <Trans t={t} i18nKey="notificationTitle" components={[highlight]} />
          }
          description={t("notificationDescription")}
        />

        <ol className="flex w-full flex-col gap-3">
          {stepKeys.map((stepKey, index) => (
            <li
              key={stepKey}
              className="flex w-full gap-2 text-body-base leading-md text-foreground-primary"
            >
              <span className="size-6 shrink-0">{`${index + 1}.`}</span>
              <span className="min-w-0 flex-1">
                <Trans
                  t={t}
                  i18nKey={stepKey}
                  components={[
                    <span key="emphasis" className="font-semibold" />,
                  ]}
                />
              </span>
            </li>
          ))}
        </ol>

        <p className="w-full py-6 text-center text-body-sm font-semibold leading-none text-foreground-tertiary">
          {t("footer")}
        </p>
      </CardContainer>
    </section>
  );
};
